import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentsApi } from '@/api/payments';
import { filmmakerApi } from '@/api/filmmaker';
import { usersApi } from '@/api/users';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BankDetails {
  account_name: string;
  account_number: string;
  sort_code: string;
  banking_id: string;
  stripe_account_id?: string;
  stripe_onboarding_completed?: boolean;
}

interface FilmmakerProfile {
  filmmaker_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

// Hook to get filmmaker banking details by filmmaker ID
export const useFilmmakerBanking = (filmmakerId: string | null) => {
  return useQuery({
    queryKey: ['filmmaker-banking', filmmakerId],
    queryFn: async (): Promise<BankDetails | null> => {
      if (!filmmakerId) return null;

      try {
        // Get filmmaker details to get user_id
        const filmmakerData = await filmmakerApi.getById(filmmakerId) as any;
        const userData = await usersApi.getById(filmmakerData.user_id) as any;
        
        // Get banking details using payments API
        const bankingDetails = await paymentsApi.getFilmmakerBanking(userData.user_id);
        
        // Find the default bank account
        const defaultAccount = bankingDetails.find(account => account.is_default);
        
        if (!defaultAccount) return null;

        return {
          account_name: defaultAccount.account_name,
          account_number: defaultAccount.account_number,
          sort_code: defaultAccount.sort_code,
          banking_id: defaultAccount.banking_id,
          stripe_account_id: (defaultAccount as any).stripe_account_id || undefined,
          stripe_onboarding_completed: (defaultAccount as any).stripe_onboarding_completed || undefined,
        };
      } catch (error) {
        console.error('Error fetching bank details:', error);
        return null;
      }
    },
    enabled: !!filmmakerId,
  });
};

// Hook to get filmmaker profile and banking info from payment ID
export const useFilmmakerFromPayment = (paymentId: string | null) => {
  return useQuery({
    queryKey: ['filmmaker-from-payment', paymentId],
    queryFn: async (): Promise<{
      filmmaker: FilmmakerProfile;
      banking: BankDetails | null;
    } | null> => {
      if (!paymentId) return null;

      try {
        // Get payment details to find filmmaker_id using payments API
        const paymentData = await paymentsApi.getById(paymentId) as any;
        
        if (!paymentData || !paymentData.filmmaker_id) {
          console.error('Payment not found');
          return null;
        }

        // Get filmmaker details
        const filmmakerData = await filmmakerApi.getById(paymentData.filmmaker_id) as any;
        const userData = await usersApi.getById(filmmakerData.user_id) as any;

        // Get banking details using payments API
        const bankingDetails = await paymentsApi.getFilmmakerBanking(filmmakerData.user_id);
        const defaultAccount = bankingDetails.find(account => account.is_default);

        const filmmakerProfile: FilmmakerProfile = {
          filmmaker_id: paymentData.filmmaker_id,
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
        };

        const bankingData: BankDetails | null = defaultAccount ? {
          account_name: defaultAccount.account_name,
          account_number: defaultAccount.account_number,
          sort_code: defaultAccount.sort_code,
          banking_id: defaultAccount.banking_id,
          stripe_account_id: (defaultAccount as any).stripe_account_id || undefined,
          stripe_onboarding_completed: (defaultAccount as any).stripe_onboarding_completed || undefined,
        } : null;

        return {
          filmmaker: filmmakerProfile,
          banking: bankingData,
        };
      } catch (error) {
        console.error('Error fetching filmmaker from payment:', error);
        return null;
      }
    },
    enabled: !!paymentId,
  });
};

// Hook to get current user's filmmaker profile  
export const useCurrentFilmmakerProfile = () => {
  return useQuery({
    queryKey: ['current-filmmaker-profile'],
    queryFn: async (): Promise<FilmmakerProfile | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        // Get user profile
        const userData = await usersApi.getById(user.id) as any;
        
        // Get filmmaker data
        const filmmakerData = await filmmakerApi.findByUser(user.id) as any;
        
        if (!filmmakerData || filmmakerData.length === 0) return null;
        
        const filmmaker = Array.isArray(filmmakerData) ? filmmakerData[0] : filmmakerData;

        return {
          filmmaker_id: filmmaker.filmmaker_id,
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
        };
      } catch (error) {
        console.error('Error fetching current filmmaker profile:', error);
        return null;
      }
    },
  });
};

// Hook to update payment approval status
export const useUpdatePaymentApproval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string) => {
      // Use the dedicated payments API endpoint for payment approval
      return await paymentsApi.updatePaymentApproval(paymentId, true);
    },
    onSuccess: () => {
      // Invalidate and refetch payment-related queries
      queryClient.invalidateQueries({
        queryKey: ['admin-payment-transactions'],
      });
      queryClient.invalidateQueries({ queryKey: ['admin-payment-stats'] });
      // Don't show toast here - let the parent component handle it
    },
    onError: (error) => {
      toast.error('Failed to approve payment');
      console.error('Error updating payment status:', error);
    },
  });
};
